<template>
    <section class="acc-glass rounded-2xl border border-foxy-border/80 p-5" aria-labelledby="profile-demographics-heading">
        <div class="text-sm font-extrabold text-foxy-text">
            <h2 id="profile-demographics-heading">Місце проживання</h2>
        </div>
        <p id="profile-demographics-description" class="mt-2 text-sm text-foxy-text-secondary">
            Необов’язково. Допоможе з локальними рекомендаціями та можливостями поруч.
        </p>

        <form
            class="mt-4 space-y-4"
            aria-describedby="profile-demographics-description profile-demographics-privacy"
            @submit.prevent="saveDemographics"
        >
            <div ref="countryCombobox">
                <label for="profile-residence-country" class="mb-1 block text-xs font-semibold text-foxy-text">Країна проживання</label>
                <div class="relative">
                    <input
                        id="profile-residence-country"
                        ref="countryInput"
                        v-model="countryQuery"
                        type="search"
                        class="foxy-input text-sm"
                        placeholder="Почніть вводити назву країни"
                        autocomplete="off"
                        role="combobox"
                        aria-autocomplete="list"
                        aria-haspopup="listbox"
                        aria-controls="profile-residence-country-results"
                        :disabled="countriesLoading || saving"
                        :aria-expanded="countryResultsOpen ? 'true' : 'false'"
                        :aria-activedescendant="activeCountryOptionId"
                        :aria-invalid="fieldErrors.geo_country_id ? 'true' : 'false'"
                        aria-describedby="profile-residence-country-status profile-residence-country-error"
                        @focus="openCountryResults"
                        @input="onCountryInput"
                        @keydown.down.prevent="moveActiveCountry(1)"
                        @keydown.up.prevent="moveActiveCountry(-1)"
                        @keydown.enter.prevent="selectActiveCountry"
                        @keydown.esc.prevent.stop="closeCountryResults"
                    />
                    <button
                        v-if="countryQuery || form.geo_country_id"
                        type="button"
                        class="absolute inset-y-0 right-2 my-auto inline-flex h-9 w-9 items-center justify-center rounded-lg text-foxy-text-secondary hover:bg-foxy-elevated focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                        aria-label="Очистити країну"
                        :disabled="saving"
                        @click="clearCountry(true)"
                    >
                        <i class="fas fa-times" aria-hidden="true"></i>
                    </button>
                </div>
                <ul
                    v-if="countryResultsOpen && filteredCountryOptions.length"
                    id="profile-residence-country-results"
                    class="mt-2 max-h-64 space-y-1 overflow-y-auto rounded-xl border border-foxy-border bg-foxy-surface p-1 shadow-xl"
                    role="listbox"
                    aria-label="Країни"
                >
                    <li
                        v-for="(country, index) in filteredCountryOptions"
                        :id="countryOptionId(country)"
                        :key="country.id"
                        class="rounded-lg px-3 py-2.5 text-sm text-foxy-text transition"
                        :class="[
                            country.saved_only ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                            index === activeCountryIndex && !country.saved_only ? 'bg-foxy-elevated' : 'hover:bg-foxy-elevated/70',
                        ]"
                        role="option"
                        :aria-disabled="country.saved_only ? 'true' : 'false'"
                        :aria-selected="form.geo_country_id === country.id ? 'true' : 'false'"
                        @mouseenter="activateCountryOption(index)"
                        @mousedown.prevent
                        @click="selectCountry(country)"
                    >
                        {{ country.display_name }}{{ country.saved_only ? ' (збережене значення)' : '' }}
                    </li>
                </ul>
                <p v-if="countriesLoading" id="profile-residence-country-status" class="mt-1 text-xs text-foxy-text-secondary" role="status">
                    Завантажуємо країни…
                </p>
                <div
                    v-else-if="countriesError"
                    id="profile-residence-country-status"
                    class="mt-2 rounded-xl border border-red-400/40 bg-red-500/10 p-3"
                    role="alert"
                >
                    <p class="text-sm text-red-700 dark:text-red-200">{{ countriesError }}</p>
                    <button type="button" class="acc-btn mt-2" @click="loadCountries">Спробувати ще раз</button>
                </div>
                <p
                    v-else-if="countryResultsOpen && countriesLoaded && filteredCountryOptions.length === 0"
                    id="profile-residence-country-status"
                    class="mt-1 text-xs text-foxy-text-secondary"
                    role="status"
                >
                    Нічого не знайдено.
                </p>
                <p
                    v-if="fieldErrors.geo_country_id"
                    id="profile-residence-country-error"
                    class="mt-1 text-xs text-red-700 dark:text-red-200"
                    role="alert"
                >
                    {{ fieldErrors.geo_country_id }}
                </p>
            </div>

            <div ref="subdivisionCombobox">
                <label for="profile-residence-subdivision" class="mb-1 block text-xs font-semibold text-foxy-text">
                    Область / провінція / регіон
                </label>
                <div class="relative">
                    <input
                        id="profile-residence-subdivision"
                        ref="subdivisionInput"
                        v-model="subdivisionQuery"
                        type="search"
                        class="foxy-input text-sm"
                        :placeholder="subdivisionPlaceholder"
                        autocomplete="off"
                        role="combobox"
                        aria-autocomplete="list"
                        aria-haspopup="listbox"
                        aria-controls="profile-residence-subdivision-results"
                        :disabled="!canFilterSubdivisions || saving"
                        :aria-expanded="subdivisionResultsOpen ? 'true' : 'false'"
                        :aria-activedescendant="activeSubdivisionOptionId"
                        :aria-invalid="fieldErrors.geo_subdivision_id ? 'true' : 'false'"
                        aria-describedby="profile-residence-subdivision-status profile-residence-subdivision-error"
                        @focus="openSubdivisionResults"
                        @input="onSubdivisionInput"
                        @keydown.down.prevent="moveActiveSubdivision(1)"
                        @keydown.up.prevent="moveActiveSubdivision(-1)"
                        @keydown.enter.prevent="selectActiveSubdivision"
                        @keydown.esc.prevent.stop="closeSubdivisionResults"
                    />
                    <button
                        v-if="subdivisionQuery || form.geo_subdivision_id"
                        type="button"
                        class="absolute inset-y-0 right-2 my-auto inline-flex h-9 w-9 items-center justify-center rounded-lg text-foxy-text-secondary hover:bg-foxy-elevated focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                        aria-label="Очистити регіон"
                        :disabled="saving"
                        @click="clearSubdivision(true)"
                    >
                        <i class="fas fa-times" aria-hidden="true"></i>
                    </button>
                </div>
                <ul
                    v-if="subdivisionResultsOpen && filteredSubdivisionOptions.length"
                    id="profile-residence-subdivision-results"
                    class="mt-2 max-h-64 space-y-1 overflow-y-auto rounded-xl border border-foxy-border bg-foxy-surface p-1 shadow-xl"
                    role="listbox"
                    aria-label="Регіони"
                >
                    <li
                        v-for="(subdivision, index) in filteredSubdivisionOptions"
                        :id="subdivisionOptionId(subdivision)"
                        :key="subdivision.id"
                        class="rounded-lg px-3 py-2.5 text-sm text-foxy-text transition"
                        :class="[
                            subdivision.saved_only ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                            index === activeSubdivisionIndex && !subdivision.saved_only ? 'bg-foxy-elevated' : 'hover:bg-foxy-elevated/70',
                        ]"
                        role="option"
                        :aria-disabled="subdivision.saved_only ? 'true' : 'false'"
                        :aria-selected="form.geo_subdivision_id === subdivision.id ? 'true' : 'false'"
                        @mouseenter="activateSubdivisionOption(index)"
                        @mousedown.prevent
                        @click="selectSubdivision(subdivision)"
                    >
                        {{ subdivision.display_name }}{{ subdivision.saved_only ? ' (збережене значення)' : '' }}
                    </li>
                </ul>
                <p v-if="subdivisionsLoading" id="profile-residence-subdivision-status" class="mt-1 text-xs text-foxy-text-secondary" role="status">
                    Завантажуємо регіони…
                </p>
                <div
                    v-else-if="subdivisionsError"
                    id="profile-residence-subdivision-status"
                    class="mt-2 rounded-xl border border-red-400/40 bg-red-500/10 p-3"
                    role="alert"
                >
                    <p class="text-sm text-red-700 dark:text-red-200">{{ subdivisionsError }}</p>
                    <button type="button" class="acc-btn mt-2" @click="loadSubdivisions">Спробувати ще раз</button>
                </div>
                <p
                    v-else-if="form.geo_country_id && subdivisionsLoaded && subdivisions.length === 0"
                    id="profile-residence-subdivision-status"
                    class="mt-1 text-xs text-foxy-text-secondary"
                >
                    Для цієї країни список регіонів відсутній. Місто можна шукати за країною.
                </p>
                <p
                    v-else-if="subdivisionResultsOpen && filteredSubdivisionOptions.length === 0"
                    id="profile-residence-subdivision-status"
                    class="mt-1 text-xs text-foxy-text-secondary"
                    role="status"
                >
                    Нічого не знайдено.
                </p>
                <p
                    v-if="fieldErrors.geo_subdivision_id"
                    id="profile-residence-subdivision-error"
                    class="mt-1 text-xs text-red-700 dark:text-red-200"
                    role="alert"
                >
                    {{ fieldErrors.geo_subdivision_id }}
                </p>
            </div>

            <div ref="placeCombobox">
                <label for="profile-residence-place" class="mb-1 block text-xs font-semibold text-foxy-text">Місто</label>
                <div class="relative">
                    <input
                        id="profile-residence-place"
                        ref="placeInput"
                        v-model="placeQuery"
                        type="search"
                        maxlength="80"
                        class="foxy-input text-sm"
                        :placeholder="placePlaceholder"
                        autocomplete="off"
                        role="combobox"
                        aria-autocomplete="list"
                        aria-haspopup="listbox"
                        :disabled="!canSearchPlaces || saving"
                        :aria-expanded="placeResultsOpen ? 'true' : 'false'"
                        :aria-activedescendant="activePlaceOptionId"
                        :aria-invalid="fieldErrors.geo_place_id ? 'true' : 'false'"
                        aria-controls="profile-residence-place-results"
                        aria-describedby="profile-residence-place-status profile-residence-place-error"
                        @input="onPlaceInput"
                        @keydown.down.prevent="moveActivePlace(1)"
                        @keydown.up.prevent="moveActivePlace(-1)"
                        @keydown.enter.prevent="selectActivePlace"
                        @keydown.esc.prevent.stop="closePlaceResults"
                    />
                    <button
                        v-if="placeQuery || form.geo_place_id"
                        type="button"
                        class="absolute inset-y-0 right-2 my-auto inline-flex h-9 w-9 items-center justify-center rounded-lg text-foxy-text-secondary hover:bg-foxy-elevated focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                        aria-label="Очистити місто"
                        :disabled="saving"
                        @click="clearPlace(true)"
                    >
                        <i class="fas fa-times" aria-hidden="true"></i>
                    </button>
                </div>

                <ul
                    v-if="placeResultsOpen && placeResults.length"
                    id="profile-residence-place-results"
                    class="mt-2 max-h-64 space-y-1 overflow-y-auto rounded-xl border border-foxy-border bg-foxy-surface p-1 shadow-xl"
                    role="listbox"
                    aria-label="Знайдені міста"
                >
                    <li
                        v-for="(place, index) in placeResults"
                        :id="placeOptionId(place)"
                        :key="place.id"
                        class="cursor-pointer rounded-lg px-3 py-2.5 text-sm text-foxy-text transition"
                        :class="index === activePlaceIndex ? 'bg-foxy-elevated' : 'hover:bg-foxy-elevated/70'"
                        role="option"
                        :aria-selected="index === activePlaceIndex ? 'true' : 'false'"
                        @mouseenter="activePlaceIndex = index"
                        @mousedown.prevent
                        @click="selectPlace(place)"
                    >
                        <span class="block font-semibold">{{ place.display_name }}</span>
                        <span v-if="placeContext(place)" class="mt-0.5 block text-xs text-foxy-text-secondary">
                            {{ placeContext(place) }}
                        </span>
                    </li>
                </ul>

                <p v-if="placeLoading" id="profile-residence-place-status" class="mt-1 text-xs text-foxy-text-secondary" role="status">
                    Шукаємо міста…
                </p>
                <div
                    v-else-if="placeSearchError"
                    id="profile-residence-place-status"
                    class="mt-2 rounded-xl border border-red-400/40 bg-red-500/10 p-3"
                    role="alert"
                >
                    <p class="text-sm text-red-700 dark:text-red-200">{{ placeSearchError }}</p>
                    <button type="button" class="acc-btn mt-2" @click="retryPlaceSearch">Спробувати пошук ще раз</button>
                </div>
                <p
                    v-else-if="placeResultsOpen && normalizedPlaceQuery.length >= PLACE_QUERY_MIN_LENGTH && placeResults.length === 0"
                    id="profile-residence-place-status"
                    class="mt-1 text-xs text-foxy-text-secondary"
                    role="status"
                >
                    Міст не знайдено.
                </p>
                <p v-else id="profile-residence-place-status" class="mt-1 text-xs text-foxy-text-secondary">
                    {{ placeGuidance }}
                </p>
                <p
                    v-if="fieldErrors.geo_place_id"
                    id="profile-residence-place-error"
                    class="mt-1 text-xs text-red-700 dark:text-red-200"
                    role="alert"
                >
                    {{ fieldErrors.geo_place_id }}
                </p>
            </div>

            <div>
                <label for="profile-birth-year" class="mb-1 block text-xs font-semibold text-foxy-text">Рік народження</label>
                <input
                    id="profile-birth-year"
                    v-model="form.birth_year"
                    type="number"
                    inputmode="numeric"
                    step="1"
                    :min="birthYearMin"
                    :max="birthYearMax"
                    class="foxy-input text-sm"
                    placeholder="Наприклад, 1990"
                    :disabled="saving"
                    :aria-invalid="fieldErrors.birth_year ? 'true' : 'false'"
                    aria-describedby="profile-birth-year-error"
                    @input="clearFieldError('birth_year')"
                />
                <p v-if="fieldErrors.birth_year" id="profile-birth-year-error" class="mt-1 text-xs text-red-700 dark:text-red-200" role="alert">
                    {{ fieldErrors.birth_year }}
                </p>
            </div>

            <p id="profile-demographics-privacy" class="text-xs text-foxy-text-secondary">
                Ці дані необов’язкові й не публікуються у профілі автоматично.
            </p>

            <p v-if="saveError" class="text-sm text-red-700 dark:text-red-200" role="alert">{{ saveError }}</p>
            <p v-if="successMessage" class="text-sm text-emerald-700 dark:text-emerald-300" role="status">{{ successMessage }}</p>

            <button
                type="submit"
                class="acc-btn-primary w-full justify-center disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="!dirty || saving"
            >
                {{ saving ? 'Збереження…' : 'Зберегти' }}
            </button>
        </form>
    </section>
</template>

<script lang="ts">
import axios from 'axios';

const PLACE_SEARCH_DELAY = 350;
const PLACE_QUERY_MIN_LENGTH = 2;
const DEMOGRAPHIC_FIELDS = ['geo_country_id', 'geo_subdivision_id', 'geo_place_id', 'birth_year'];

function emptyFieldErrors() {
    return {
        geo_country_id: '',
        geo_subdivision_id: '',
        geo_place_id: '',
        birth_year: '',
    };
}

export default {
    name: 'SettingsProfileDemographics',
    props: {
        demographics: { type: Object, default: null },
    },
    data() {
        const currentYear = new Date().getFullYear();

        return {
            PLACE_QUERY_MIN_LENGTH,
            birthYearMin: currentYear - 130,
            birthYearMax: currentYear,
            form: {
                geo_country_id: null,
                geo_subdivision_id: null,
                geo_place_id: null,
                birth_year: '',
            },
            snapshot: {
                geo_country_id: null,
                geo_subdivision_id: null,
                geo_place_id: null,
                birth_year: null,
            },
            savedDetails: {
                country: null,
                subdivision: null,
                place: null,
            },
            countryQuery: '',
            countryResultsOpen: false,
            activeCountryIndex: 0,
            countries: [],
            countriesLoading: false,
            countriesLoaded: false,
            countriesError: '',
            countriesRequestId: 0,
            subdivisions: [],
            subdivisionsLoading: false,
            subdivisionsLoaded: false,
            subdivisionsError: '',
            subdivisionsRequestId: 0,
            subdivisionQuery: '',
            subdivisionResultsOpen: false,
            activeSubdivisionIndex: 0,
            selectedPlace: null,
            placeQuery: '',
            placeResults: [],
            placeResultsOpen: false,
            placeLoading: false,
            placeSearchError: '',
            placeSearchTimer: null,
            placeRequestId: 0,
            activePlaceIndex: 0,
            dismissListenersAttached: false,
            saving: false,
            saveRequestId: 0,
            saveError: '',
            successMessage: '',
            fieldErrors: emptyFieldErrors(),
        };
    },
    computed: {
        countryOptions() {
            const options = this.countries.map((country) => ({ ...country, saved_only: false }));
            const saved = this.savedDetails.country;
            if (saved && !options.some((country) => this.nullableId(country.id) === this.nullableId(saved.id))) {
                options.push({ ...saved, saved_only: true });
            }

            return options;
        },
        filteredCountryOptions() {
            const query = this.normalizedLocalQuery(this.countryQuery);
            if (!query) return this.countryOptions;

            return this.countryOptions.filter((country) => {
                const names = [country.display_name, ...(Array.isArray(country.search_names) ? country.search_names : [])];
                const matchesName = names.some((name) => this.normalizedLocalQuery(name).includes(query));
                const iso2 = this.normalizedLocalQuery(country.iso2);
                return matchesName || iso2 === query;
            });
        },
        subdivisionOptions() {
            const options = this.subdivisions.map((subdivision) => ({ ...subdivision, saved_only: false }));
            const saved = this.savedDetails.subdivision;
            const savedCountryId = this.nullableId(this.savedDetails.country?.id);
            if (
                saved &&
                savedCountryId === this.nullableId(this.form.geo_country_id) &&
                !options.some((subdivision) => this.nullableId(subdivision.id) === this.nullableId(saved.id))
            ) {
                options.push({ ...saved, saved_only: true });
            }

            return options;
        },
        filteredSubdivisionOptions() {
            const query = this.normalizedLocalQuery(this.subdivisionQuery);
            if (!query) return this.subdivisionOptions;

            return this.subdivisionOptions.filter((subdivision) => {
                const names = [subdivision.display_name, ...(Array.isArray(subdivision.search_names) ? subdivision.search_names : [])];
                return names.some((name) => this.normalizedLocalQuery(name).includes(query));
            });
        },
        subdivisionPlaceholder() {
            if (!this.form.geo_country_id) return 'Спочатку виберіть країну';
            if (this.subdivisionsLoading) return 'Завантаження…';
            if (this.subdivisionsError) return 'Не вдалося завантажити';
            if (this.subdivisionsLoaded && this.subdivisions.length === 0) return 'Для країни немає регіонів';
            return 'Почніть вводити назву регіону';
        },
        canFilterSubdivisions() {
            return this.selectedCountryIsActive && this.subdivisionsLoaded && !this.subdivisionsError && this.subdivisions.length > 0;
        },
        selectedCountryIsActive() {
            return this.countries.some((country) => this.nullableId(country.id) === this.nullableId(this.form.geo_country_id));
        },
        selectedSubdivisionIsActive() {
            return this.subdivisions.some((subdivision) => this.nullableId(subdivision.id) === this.nullableId(this.form.geo_subdivision_id));
        },
        canSearchPlaces() {
            if (!this.selectedCountryIsActive || !this.subdivisionsLoaded || this.subdivisionsError) return false;
            if (this.subdivisions.length === 0) return this.form.geo_subdivision_id === null;
            return this.selectedSubdivisionIsActive;
        },
        placePlaceholder() {
            if (!this.form.geo_country_id) return 'Спочатку виберіть країну';
            if (!this.selectedCountryIsActive) return 'Виберіть країну з актуального списку';
            if (this.subdivisionsLoading || (!this.subdivisionsLoaded && !this.subdivisionsError)) return 'Завантажуємо регіони…';
            if (this.subdivisionsError) return 'Спочатку повторіть завантаження регіонів';
            if (this.subdivisions.length > 0 && !this.selectedSubdivisionIsActive) return 'Спочатку виберіть регіон';
            return 'Почніть вводити назву міста';
        },
        normalizedPlaceQuery() {
            return typeof this.placeQuery === 'string' ? this.placeQuery.trim() : '';
        },
        placeGuidance() {
            if (!this.form.geo_country_id) return 'Спочатку виберіть країну.';
            if (!this.selectedCountryIsActive) return 'Щоб шукати місто, виберіть країну з актуального списку.';
            if (this.subdivisionsLoading) return 'Зачекайте, поки завантажаться регіони.';
            if (this.subdivisionsError) return 'Спочатку повторіть завантаження регіонів.';
            if (this.subdivisions.length > 0 && !this.selectedSubdivisionIsActive) {
                return 'Спочатку виберіть область, провінцію або регіон.';
            }
            if (this.normalizedPlaceQuery.length < PLACE_QUERY_MIN_LENGTH) {
                return `Введіть щонайменше ${PLACE_QUERY_MIN_LENGTH} символи.`;
            }
            return 'Оберіть місто зі списку результатів.';
        },
        activePlaceOptionId() {
            const place = this.placeResults[this.activePlaceIndex];
            return place ? this.placeOptionId(place) : undefined;
        },
        activeCountryOptionId() {
            const country = this.filteredCountryOptions[this.activeCountryIndex];
            return country && !country.saved_only ? this.countryOptionId(country) : undefined;
        },
        activeSubdivisionOptionId() {
            const subdivision = this.filteredSubdivisionOptions[this.activeSubdivisionIndex];
            return subdivision && !subdivision.saved_only ? this.subdivisionOptionId(subdivision) : undefined;
        },
        dirty() {
            const current = this.currentState();
            return DEMOGRAPHIC_FIELDS.some((field) => current[field] !== this.snapshot[field]);
        },
    },
    watch: {
        demographics: {
            immediate: true,
            handler(value) {
                this.hydrateDemographics(value);
            },
        },
    },
    mounted() {
        this.loadCountries();
    },
    methods: {
        nullableId(value) {
            if (value === null || value === undefined || value === '') return null;
            const parsed = Number(value);
            return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
        },
        normalizedLocalQuery(value) {
            return String(value || '')
                .normalize('NFKC')
                .trim()
                .toLocaleLowerCase('uk-UA');
        },
        normalizedBirthYear() {
            if (this.form.birth_year === null || this.form.birth_year === undefined || this.form.birth_year === '') return null;
            return Number(this.form.birth_year);
        },
        currentState() {
            return {
                geo_country_id: this.nullableId(this.form.geo_country_id),
                geo_subdivision_id: this.nullableId(this.form.geo_subdivision_id),
                geo_place_id: this.nullableId(this.form.geo_place_id),
                birth_year: this.normalizedBirthYear(),
            };
        },
        hydrateDemographics(value) {
            const demographics = value || {};
            const state = {
                geo_country_id: this.nullableId(demographics.geo_country_id),
                geo_subdivision_id: this.nullableId(demographics.geo_subdivision_id),
                geo_place_id: this.nullableId(demographics.geo_place_id),
                birth_year: demographics.birth_year === null || demographics.birth_year === undefined ? null : Number(demographics.birth_year),
            };

            this.form.geo_country_id = state.geo_country_id;
            this.form.geo_subdivision_id = state.geo_subdivision_id;
            this.form.geo_place_id = state.geo_place_id;
            this.form.birth_year = state.birth_year === null ? '' : String(state.birth_year);
            this.snapshot = { ...state };
            this.savedDetails = {
                country: demographics.country ? { ...demographics.country } : null,
                subdivision: demographics.subdivision ? { ...demographics.subdivision } : null,
                place: demographics.place ? { ...demographics.place } : null,
            };
            this.countryQuery = this.savedDetails.country?.display_name || '';
            this.countryResultsOpen = false;
            this.activeCountryIndex = 0;
            this.subdivisionQuery = this.savedDetails.subdivision?.display_name || '';
            this.subdivisionResultsOpen = false;
            this.activeSubdivisionIndex = 0;
            this.selectedPlace = demographics.place
                ? {
                      ...demographics.place,
                      country: demographics.country ? { ...demographics.country } : null,
                      subdivision: demographics.subdivision ? { ...demographics.subdivision } : null,
                      admin2_context: null,
                  }
                : null;
            this.placeQuery = this.selectedPlace ? this.placeOptionLabel(this.selectedPlace) : '';
            this.resetPlaceSearchState();
            this.fieldErrors = emptyFieldErrors();
            this.saveError = '';

            if (this.countriesLoaded && state.geo_country_id !== null) {
                this.loadSubdivisions();
            }
        },
        async loadCountries() {
            const requestId = ++this.countriesRequestId;
            this.countriesLoading = true;
            this.countriesError = '';

            try {
                const response = await axios.get('/api/account/settings/geo/countries');
                if (requestId !== this.countriesRequestId) return;
                this.countries = Array.isArray(response.data?.data) ? response.data.data : [];
                this.countriesLoaded = true;

                if (this.form.geo_country_id !== null) {
                    await this.loadSubdivisions();
                }
            } catch {
                if (requestId !== this.countriesRequestId) return;
                this.countries = [];
                this.countriesLoaded = false;
                this.countriesError = 'Не вдалося завантажити країни. Спробуйте ще раз.';
            } finally {
                if (requestId === this.countriesRequestId) this.countriesLoading = false;
            }
        },
        async loadSubdivisions() {
            const countryId = this.nullableId(this.form.geo_country_id);
            const requestId = ++this.subdivisionsRequestId;
            this.subdivisions = [];
            this.subdivisionsLoaded = false;
            this.subdivisionsError = '';
            this.subdivisionResultsOpen = false;
            this.activeSubdivisionIndex = 0;

            if (countryId === null) return;
            this.subdivisionsLoading = true;

            try {
                const response = await axios.get(`/api/account/settings/geo/countries/${countryId}/subdivisions`);
                if (requestId !== this.subdivisionsRequestId || countryId !== this.nullableId(this.form.geo_country_id)) return;
                this.subdivisions = Array.isArray(response.data?.data) ? response.data.data : [];
                this.subdivisionsLoaded = true;
            } catch {
                if (requestId !== this.subdivisionsRequestId || countryId !== this.nullableId(this.form.geo_country_id)) return;
                this.subdivisions = [];
                this.subdivisionsLoaded = false;
                this.subdivisionsError = 'Не вдалося завантажити регіони. Спробуйте ще раз.';
                this.subdivisionResultsOpen = false;
            } finally {
                if (requestId === this.subdivisionsRequestId) this.subdivisionsLoading = false;
            }
        },
        openCountryResults() {
            if (!this.countriesLoaded || this.countriesError) return;
            this.countryResultsOpen = true;
            this.activeCountryIndex = this.preferredOptionIndex(this.filteredCountryOptions, this.form.geo_country_id);
            this.syncComboboxDismissListeners();
        },
        closeCountryResults() {
            this.countryResultsOpen = false;
            this.activeCountryIndex = 0;
            this.syncComboboxDismissListeners();
        },
        onCountryInput() {
            const selected = this.countryOptions.find((country) => this.nullableId(country.id) === this.nullableId(this.form.geo_country_id));
            if (selected && this.normalizedLocalQuery(this.countryQuery) !== this.normalizedLocalQuery(selected.display_name)) {
                this.invalidateCountrySelection();
            }

            this.clearFieldError('geo_country_id');
            this.successMessage = '';
            this.countryResultsOpen = this.countriesLoaded && !this.countriesError;
            this.activeCountryIndex = this.preferredOptionIndex(this.filteredCountryOptions, null);
            this.syncComboboxDismissListeners();
        },
        moveActiveCountry(direction) {
            if (!this.countryResultsOpen) this.openCountryResults();
            this.activeCountryIndex = this.moveActiveOption(this.filteredCountryOptions, this.activeCountryIndex, direction);
            this.scrollActiveOption(this.activeCountryOptionId);
        },
        activateCountryOption(index) {
            if (!this.filteredCountryOptions[index]?.saved_only) this.activeCountryIndex = index;
        },
        selectActiveCountry() {
            if (!this.countryResultsOpen) return;
            const country = this.filteredCountryOptions[this.activeCountryIndex];
            if (country) this.selectCountry(country);
        },
        selectCountry(country) {
            if (!country || country.saved_only) return;
            const countryId = this.nullableId(country.id);
            if (countryId === null) return;
            const changed = countryId !== this.nullableId(this.form.geo_country_id);
            this.form.geo_country_id = countryId;
            this.countryQuery = String(country.display_name || '');
            this.closeCountryResults();
            this.clearFieldError('geo_country_id');
            this.successMessage = '';

            if (!changed) return;
            this.resetSubdivisionForCountryChange();
            this.loadSubdivisions();
        },
        invalidateCountrySelection() {
            if (this.form.geo_country_id === null) return;
            this.form.geo_country_id = null;
            this.resetSubdivisionForCountryChange();
        },
        clearCountry(returnFocus = false) {
            this.form.geo_country_id = null;
            this.countryQuery = '';
            this.closeCountryResults();
            this.resetSubdivisionForCountryChange();
            this.clearFieldError('geo_country_id');
            this.successMessage = '';
            if (returnFocus)
                this.$nextTick(() => {
                    this.$refs.countryInput?.focus();
                    this.closeCountryResults();
                });
        },
        resetSubdivisionForCountryChange() {
            this.subdivisionsRequestId += 1;
            this.subdivisionsLoading = false;
            this.form.geo_subdivision_id = null;
            this.subdivisionQuery = '';
            this.subdivisionResultsOpen = false;
            this.activeSubdivisionIndex = 0;
            this.clearPlace(false);
            this.subdivisions = [];
            this.subdivisionsLoaded = false;
            this.subdivisionsError = '';
            this.clearFieldError('geo_subdivision_id');
        },
        openSubdivisionResults() {
            if (!this.canFilterSubdivisions) return;
            this.subdivisionResultsOpen = true;
            this.activeSubdivisionIndex = this.preferredOptionIndex(this.filteredSubdivisionOptions, this.form.geo_subdivision_id);
            this.syncComboboxDismissListeners();
        },
        closeSubdivisionResults() {
            this.subdivisionResultsOpen = false;
            this.activeSubdivisionIndex = 0;
            this.syncComboboxDismissListeners();
        },
        onSubdivisionInput() {
            const selected = this.subdivisionOptions.find(
                (subdivision) => this.nullableId(subdivision.id) === this.nullableId(this.form.geo_subdivision_id),
            );
            if (selected && this.normalizedLocalQuery(this.subdivisionQuery) !== this.normalizedLocalQuery(selected.display_name)) {
                this.invalidateSubdivisionSelection();
            }

            this.clearFieldError('geo_subdivision_id');
            this.successMessage = '';
            this.subdivisionResultsOpen = this.canFilterSubdivisions;
            this.activeSubdivisionIndex = this.preferredOptionIndex(this.filteredSubdivisionOptions, null);
            this.syncComboboxDismissListeners();
        },
        moveActiveSubdivision(direction) {
            if (!this.subdivisionResultsOpen) this.openSubdivisionResults();
            this.activeSubdivisionIndex = this.moveActiveOption(this.filteredSubdivisionOptions, this.activeSubdivisionIndex, direction);
            this.scrollActiveOption(this.activeSubdivisionOptionId);
        },
        activateSubdivisionOption(index) {
            if (!this.filteredSubdivisionOptions[index]?.saved_only) this.activeSubdivisionIndex = index;
        },
        selectActiveSubdivision() {
            if (!this.subdivisionResultsOpen) return;
            const subdivision = this.filteredSubdivisionOptions[this.activeSubdivisionIndex];
            if (subdivision) this.selectSubdivision(subdivision);
        },
        selectSubdivision(subdivision) {
            if (!subdivision || subdivision.saved_only) return;
            const subdivisionId = this.nullableId(subdivision.id);
            if (subdivisionId === null) return;
            const changed = subdivisionId !== this.nullableId(this.form.geo_subdivision_id);
            this.form.geo_subdivision_id = subdivisionId;
            this.subdivisionQuery = String(subdivision.display_name || '');
            this.closeSubdivisionResults();
            this.clearFieldError('geo_subdivision_id');
            this.successMessage = '';
            if (changed) this.clearPlace(false);
        },
        invalidateSubdivisionSelection() {
            if (this.form.geo_subdivision_id === null) return;
            this.form.geo_subdivision_id = null;
            this.clearPlace(false);
        },
        clearSubdivision(returnFocus = false) {
            this.form.geo_subdivision_id = null;
            this.subdivisionQuery = '';
            this.closeSubdivisionResults();
            this.clearPlace(false);
            this.clearFieldError('geo_subdivision_id');
            this.successMessage = '';
            if (returnFocus)
                this.$nextTick(() => {
                    this.$refs.subdivisionInput?.focus();
                    this.closeSubdivisionResults();
                });
        },
        preferredOptionIndex(options, selectedId) {
            const selectedIndex = options.findIndex((option) => !option.saved_only && this.nullableId(option.id) === this.nullableId(selectedId));
            if (selectedIndex >= 0) return selectedIndex;
            const firstSelectable = options.findIndex((option) => !option.saved_only);
            return firstSelectable >= 0 ? firstSelectable : 0;
        },
        moveActiveOption(options, currentIndex, direction) {
            const selectable = options.map((option, index) => (!option.saved_only ? index : null)).filter((index) => index !== null);
            if (selectable.length === 0) return 0;
            const position = selectable.indexOf(currentIndex);
            const nextPosition =
                position < 0 ? (direction > 0 ? 0 : selectable.length - 1) : (position + direction + selectable.length) % selectable.length;
            return selectable[nextPosition];
        },
        scrollActiveOption(optionId) {
            if (!optionId) return;
            this.$nextTick(() => {
                document.getElementById(optionId)?.scrollIntoView({ block: 'nearest' });
            });
        },
        syncComboboxDismissListeners() {
            const shouldListen = this.countryResultsOpen || this.subdivisionResultsOpen || this.placeResultsOpen;
            if (shouldListen && !this.dismissListenersAttached) {
                document.addEventListener('pointerdown', this.onDocumentComboboxInteraction);
                document.addEventListener('focusin', this.onDocumentComboboxInteraction);
                this.dismissListenersAttached = true;
            } else if (!shouldListen) {
                this.detachComboboxDismissListeners();
            }
        },
        detachComboboxDismissListeners() {
            if (!this.dismissListenersAttached) return;
            document.removeEventListener('pointerdown', this.onDocumentComboboxInteraction);
            document.removeEventListener('focusin', this.onDocumentComboboxInteraction);
            this.dismissListenersAttached = false;
        },
        onDocumentComboboxInteraction(event) {
            const target = event.target;
            if (this.countryResultsOpen && !this.$refs.countryCombobox?.contains(target)) this.closeCountryResults();
            if (this.subdivisionResultsOpen && !this.$refs.subdivisionCombobox?.contains(target)) this.closeSubdivisionResults();
            if (this.placeResultsOpen && !this.$refs.placeCombobox?.contains(target)) this.closePlaceResults();
        },
        countryOptionId(country) {
            return `profile-residence-country-option-${country.id}`;
        },
        subdivisionOptionId(subdivision) {
            return `profile-residence-subdivision-option-${subdivision.id}`;
        },
        clearPlace(returnFocus = false) {
            this.form.geo_place_id = null;
            this.selectedPlace = null;
            this.placeQuery = '';
            this.resetPlaceSearchState();
            this.clearFieldError('geo_place_id');
            this.successMessage = '';
            if (returnFocus) this.$nextTick(() => this.$refs.placeInput?.focus());
        },
        resetPlaceSearchState() {
            if (this.placeSearchTimer !== null) {
                clearTimeout(this.placeSearchTimer);
                this.placeSearchTimer = null;
            }
            this.placeRequestId += 1;
            this.placeResults = [];
            this.placeResultsOpen = false;
            this.placeLoading = false;
            this.placeSearchError = '';
            this.activePlaceIndex = 0;
            this.syncComboboxDismissListeners();
        },
        onPlaceInput() {
            const selectedLabel = this.selectedPlace ? this.placeOptionLabel(this.selectedPlace) : '';
            if (this.selectedPlace && this.placeQuery !== selectedLabel) {
                this.selectedPlace = null;
                this.form.geo_place_id = null;
            }

            this.clearFieldError('geo_place_id');
            this.successMessage = '';
            this.schedulePlaceSearch();
        },
        schedulePlaceSearch() {
            if (this.placeSearchTimer !== null) clearTimeout(this.placeSearchTimer);
            const requestId = ++this.placeRequestId;
            const query = this.normalizedPlaceQuery;
            const context = this.placeContextKey();
            this.placeResults = [];
            this.placeResultsOpen = false;
            this.placeLoading = false;
            this.placeSearchError = '';
            this.syncComboboxDismissListeners();

            if (!this.canSearchPlaces || query.length < PLACE_QUERY_MIN_LENGTH) {
                this.placeSearchTimer = null;
                return;
            }

            this.placeSearchTimer = setTimeout(() => {
                this.searchPlaces(query, requestId, context);
            }, PLACE_SEARCH_DELAY);
        },
        async searchPlaces(query, requestId = ++this.placeRequestId, context = this.placeContextKey()) {
            if (!this.canSearchPlaces || query.trim().length < PLACE_QUERY_MIN_LENGTH) return;
            this.placeLoading = true;
            this.placeSearchError = '';

            try {
                const response = await axios.get('/api/account/settings/geo/places', {
                    params: {
                        q: query,
                        country_id: this.nullableId(this.form.geo_country_id),
                        subdivision_id: this.nullableId(this.form.geo_subdivision_id),
                    },
                });
                if (!this.isCurrentPlaceRequest(query, requestId, context)) return;
                this.placeResults = Array.isArray(response.data?.data) ? response.data.data : [];
                this.placeResultsOpen = true;
                this.activePlaceIndex = 0;
                this.syncComboboxDismissListeners();
            } catch {
                if (!this.isCurrentPlaceRequest(query, requestId, context)) return;
                this.placeResults = [];
                this.placeResultsOpen = false;
                this.placeSearchError = 'Не вдалося знайти міста. Спробуйте ще раз.';
                this.syncComboboxDismissListeners();
            } finally {
                if (requestId === this.placeRequestId) {
                    this.placeLoading = false;
                    this.placeSearchTimer = null;
                }
            }
        },
        retryPlaceSearch() {
            const query = this.normalizedPlaceQuery;
            if (!this.canSearchPlaces || query.length < PLACE_QUERY_MIN_LENGTH) return;
            const requestId = ++this.placeRequestId;
            this.searchPlaces(query, requestId, this.placeContextKey());
        },
        isCurrentPlaceRequest(query, requestId, context) {
            return requestId === this.placeRequestId && query === this.normalizedPlaceQuery && context === this.placeContextKey();
        },
        placeContextKey() {
            return `${this.nullableId(this.form.geo_country_id) || ''}:${this.nullableId(this.form.geo_subdivision_id) || ''}`;
        },
        moveActivePlace(direction) {
            const count = this.placeResults.length;
            if (!this.placeResultsOpen || count === 0) return;
            this.activePlaceIndex = (this.activePlaceIndex + direction + count) % count;
            this.$nextTick(() => {
                document.getElementById(this.activePlaceOptionId)?.scrollIntoView({ block: 'nearest' });
            });
        },
        selectActivePlace() {
            const place = this.placeResults[this.activePlaceIndex];
            if (this.placeResultsOpen && place) this.selectPlace(place);
        },
        selectPlace(place) {
            this.selectedPlace = { ...place };
            this.form.geo_place_id = this.nullableId(place.id);
            this.placeQuery = this.placeOptionLabel(place);
            this.placeResults = [];
            this.placeResultsOpen = false;
            this.placeSearchError = '';
            this.activePlaceIndex = 0;
            this.clearFieldError('geo_place_id');
            this.successMessage = '';
            this.syncComboboxDismissListeners();
        },
        closePlaceResults() {
            if (this.placeSearchTimer !== null) clearTimeout(this.placeSearchTimer);
            this.placeSearchTimer = null;
            this.placeRequestId += 1;
            this.placeResultsOpen = false;
            this.placeLoading = false;
            this.activePlaceIndex = 0;
            this.syncComboboxDismissListeners();
        },
        placeOptionId(place) {
            return `profile-residence-place-option-${place.id}`;
        },
        placeContext(place) {
            const name = String(place?.display_name || '').trim();
            const candidates = [place?.subdivision?.display_name, place?.admin2_context?.display_name, place?.country?.display_name];
            const seen = new Set([this.normalizedLocalQuery(name)]);

            return candidates
                .map((value) => String(value || '').trim())
                .filter((value) => {
                    const key = this.normalizedLocalQuery(value);
                    if (!key || seen.has(key)) return false;
                    seen.add(key);
                    return true;
                })
                .join(' · ');
        },
        placeOptionLabel(place) {
            return String(place?.display_name || '').trim();
        },
        clearFieldError(field) {
            if (this.fieldErrors[field]) this.fieldErrors = { ...this.fieldErrors, [field]: '' };
        },
        validateBirthYear() {
            const value = this.normalizedBirthYear();
            if (value === null) return true;
            if (!Number.isInteger(value) || value < this.birthYearMin || value > this.birthYearMax) {
                this.fieldErrors = {
                    ...this.fieldErrors,
                    birth_year: `Вкажіть цілий рік від ${this.birthYearMin} до ${this.birthYearMax}.`,
                };
                return false;
            }
            return true;
        },
        buildPatchPayload() {
            const current = this.currentState();
            return DEMOGRAPHIC_FIELDS.reduce((payload, field) => {
                if (current[field] !== this.snapshot[field]) payload[field] = current[field];
                return payload;
            }, {});
        },
        applyValidationErrors(error) {
            const serverErrors = error?.response?.data?.errors || {};
            const messages = {
                geo_country_id: 'Перевірте вибрану країну.',
                geo_subdivision_id: 'Перевірте вибраний регіон.',
                geo_place_id: 'Перевірте вибране місто.',
                birth_year: `Вкажіть цілий рік від ${this.birthYearMin} до ${this.birthYearMax}.`,
            };
            const nextErrors = emptyFieldErrors();
            let hasKnownField = false;

            Object.keys(messages).forEach((field) => {
                if (serverErrors[field]) {
                    nextErrors[field] = messages[field];
                    hasKnownField = true;
                }
            });

            this.fieldErrors = nextErrors;
            this.saveError = hasKnownField
                ? 'Перевірте позначені поля.'
                : 'Не вдалося перевірити дані профілю. Перегляньте значення та спробуйте ще раз.';
        },
        async saveDemographics() {
            if (this.saving || !this.dirty) return;
            this.fieldErrors = emptyFieldErrors();
            this.saveError = '';
            this.successMessage = '';
            if (!this.validateBirthYear()) return;

            const payload = this.buildPatchPayload();
            if (Object.keys(payload).length === 0) return;
            const requestId = ++this.saveRequestId;
            this.saving = true;

            try {
                const response = await axios.patch('/api/account/profile/demographics', payload);
                if (requestId !== this.saveRequestId) return;
                const demographics = response.data?.demographics || {};
                this.hydrateDemographics(demographics);
                this.successMessage = response.data?.message || 'Дані профілю збережено.';
                this.$emit('demographics-updated', demographics);
            } catch (error) {
                if (requestId !== this.saveRequestId) return;
                if (error?.response?.status === 422) this.applyValidationErrors(error);
                else this.saveError = 'Не вдалося зберегти дані профілю. Спробуйте ще раз.';
            } finally {
                if (requestId === this.saveRequestId) this.saving = false;
            }
        },
    },
    // Vue 2 cleanup hook; beforeUnmount is not called by this application runtime.
    // eslint-disable-next-line vue/no-deprecated-destroyed-lifecycle
    beforeDestroy() {
        if (this.placeSearchTimer !== null) clearTimeout(this.placeSearchTimer);
        this.detachComboboxDismissListeners();
        this.countriesRequestId += 1;
        this.subdivisionsRequestId += 1;
        this.placeRequestId += 1;
        this.saveRequestId += 1;
    },
};
</script>
